import { useId, useState, type Dispatch, type ReactNode, type SetStateAction } from "react";
import {
  CRM_TRANSCRIPT_MAX_LENGTH,
  CRM_TRANSCRIPT_MIN_LENGTH,
  type CrmExtraction,
  type CrmExtractionApplied,
  type CrmExtractionConfidence,
} from "@shared/modules/crm";
import { api, ApiError } from "../../../core/lib/api";
import { Badge } from "../../../core/components/ui/badge";
import { Button } from "../../../core/components/ui/button";
import { Textarea } from "../../../core/components/ui/input";
import { Modal } from "../../../core/components/ui/modal";
import { formatDateTime } from "../../../core/lib/format";
import { useToastStore } from "../../../core/stores/toast";
import { CRM_TASK_TYPE_LABELS, formatRelativeDay } from "../lib/crm";

// Bornes du schéma zod de l'API : les rappeler ici évite un aller-retour réseau
// pour un texte que le serveur refusera de toute façon.
const MIN_LENGTH = CRM_TRANSCRIPT_MIN_LENGTH;
const MAX_LENGTH = CRM_TRANSCRIPT_MAX_LENGTH;

// Même règle que le schéma partagé du dossier : une adresse mal entendue au
// téléphone ne doit pas repartir vers le serveur pour s'y faire refuser.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Clés dont la valeur doit passer un contrôle de format avant l'envoi. */
function isSendable(key: string, value: string | number | null): boolean {
  if (key !== "contactEmail") return true;
  return typeof value === "string" && EMAIL_RE.test(value);
}

/** Message d'état affiché sous la saisie : une pastille courte et une explication. */
interface Failure {
  badge: string;
  message: string;
}

const CONFIDENCE_VARIANT: Record<CrmExtractionConfidence, "success" | "neutral" | "warning"> = {
  haute: "success",
  moyenne: "neutral",
  faible: "warning",
};

const CONFIDENCE_LABELS: Record<CrmExtractionConfidence, string> = {
  haute: "Confiance haute",
  moyenne: "Confiance moyenne",
  faible: "Confiance faible",
};

interface TranscriptImportProps {
  accountId: string;
  open: boolean;
  onClose: () => void;
  onApplied: () => void;
}

/** Import d'un compte rendu : analyse, relecture case par case, application. */
export function TranscriptImport({ accountId, open, onClose, onApplied }: TranscriptImportProps) {
  const push = useToastStore((s) => s.push);
  const domId = useId();

  const [text, setText] = useState("");
  const [extraction, setExtraction] = useState<CrmExtraction | null>(null);
  // Le texte réellement analysé : c'est lui qu'on archive, même si la zone de
  // saisie a été retouchée entre l'analyse et l'application.
  const [analyzed, setAnalyzed] = useState("");
  const [reviewing, setReviewing] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);

  const [fieldSel, setFieldSel] = useState<boolean[]>([]);
  const [taskSel, setTaskSel] = useState<boolean[]>([]);
  const [contactSel, setContactSel] = useState<boolean[]>([]);
  const [confirmingClose, setConfirmingClose] = useState(false);

  const trimmed = text.trim();

  const reset = () => {
    setText("");
    setExtraction(null);
    setAnalyzed("");
    setReviewing(false);
    setFailure(null);
    setFieldSel([]);
    setTaskSel([]);
    setContactSel([]);
    setConfirmingClose(false);
  };

  // Le texte collé et l'analyse déjà payée survivent à une fermeture accidentelle :
  // on ne remet à zéro qu'après un import appliqué ou un abandon assumé.
  const abandon = () => {
    reset();
    onClose();
  };

  const requestClose = () => {
    if (analyzing || applying) return;
    // Deuxième Échap : l'intention est claire, on n'insiste pas.
    if (confirmingClose) {
      abandon();
      return;
    }
    if (trimmed.length > 0 || extraction) {
      setConfirmingClose(true);
      return;
    }
    abandon();
  };

  const analyze = async () => {
    if (analyzing || trimmed.length < MIN_LENGTH) return;
    if (trimmed.length > MAX_LENGTH) {
      setFailure({
        badge: "Texte trop long",
        message: `Le compte rendu dépasse ${MAX_LENGTH.toLocaleString("fr-CA")} caractères. Analysez-le en deux fois.`,
      });
      return;
    }

    setAnalyzing(true);
    setFailure(null);
    try {
      const result = await api<CrmExtraction>(`/api/crm/accounts/${accountId}/extract`, {
        method: "POST",
        body: JSON.stringify({ text: trimmed }),
      });
      setExtraction(result);
      setAnalyzed(trimmed);
      // Une valeur nouvelle est retenue d'office ; un écrasement demande un geste
      // explicite, un import ne doit jamais effacer une saisie manuelle tout seul.
      setFieldSel(result.fields.map((f) => !f.conflict && isSendable(f.key, f.value)));
      setTaskSel(result.tasks.map(() => true));
      // Un doublon de contact est plus destructeur qu'un champ écrasé : il est
      // invisible et ne se défait pas d'un clic. Même règle que pour les champs.
      setContactSel(result.contacts.map((c) => !c.conflict));
      setReviewing(true);
    } catch (err) {
      const next = analyzeFailure(err);
      setFailure(next);
      if (isUnexpected(err)) push("danger", "Échec de l'analyse", next.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const apply = async () => {
    if (!extraction || applying) return;
    setApplying(true);
    setFailure(null);
    try {
      const applied = await api<CrmExtractionApplied>(
        `/api/crm/accounts/${accountId}/extract/apply`,
        {
          method: "POST",
          body: JSON.stringify({
            transcript: analyzed,
            summary: extraction.summary,
            fields: extraction.fields
              // Le filtre de format s'applique aussi aux champs du dossier, et pas
              // seulement aux contacts : c'est la même adresse mal entendue.
              .filter((f, i) => fieldSel[i] && isSendable(f.key, f.value))
              .map((f) => ({ key: f.key, value: f.value })),
            tasks: extraction.tasks
              .filter((_, i) => taskSel[i])
              .map((t) => ({ title: t.title, type: t.type, dueAt: t.dueAt })),
            contacts: extraction.contacts
              .filter((_, i) => contactSel[i])
              .map((c) => ({
                name: c.name,
                role: c.role,
                // L'API valide le format : une adresse mal entendue au téléphone
                // ferait échouer la ligne, le contact vaut mieux sans elle.
                email: c.email && EMAIL_RE.test(c.email) ? c.email : null,
                phone: c.phone,
              })),
          }),
        },
      );

      push("success", "Compte rendu importé", appliedSummary(applied));
      reset();
      onApplied();
    } catch (err) {
      const rejected = rejectedKeys(err);
      // Nommer les lignes refusées, et les décocher : sans ça, l'utilisateur
      // n'avait d'autre stratégie que la dichotomie.
      if (rejected.length > 0) {
        setFieldSel((prev) =>
          prev.map((on, i) => on && !rejected.includes(extraction.fields[i]?.key ?? "")),
        );
      }
      const next = applyFailure(err, labelsFor(extraction, rejected));
      setFailure(next);
      push("danger", "Échec de l'import", next.message);
    } finally {
      setApplying(false);
    }
  };

  const selectedCount = count(fieldSel) + count(taskSel) + count(contactSel);

  return (
    <Modal
      open={open}
      title="Importer un compte rendu"
      onClose={requestClose}
      // Proposition, valeur actuelle et citation ne tiennent pas dans la largeur par défaut.
      size="lg"
      footer={
        confirmingClose ? (
          <>
            <Button variant="ghost" onClick={() => setConfirmingClose(false)}>
              Revenir au compte rendu
            </Button>
            <Button variant="ghost-danger" onClick={abandon}>
              Abandonner
            </Button>
          </>
        ) : reviewing && extraction ? (
          <>
            <Button variant="ghost" onClick={() => setReviewing(false)} disabled={applying}>
              Retour
            </Button>
            {selectedCount === 0 ? (
              <Button variant="ghost" size="sm" onClick={() => void apply()} disabled={applying}>
                Archiver sans appliquer
              </Button>
            ) : null}
            <Button
              variant="primary"
              onClick={() => void apply()}
              disabled={applying || selectedCount === 0}
            >
              {applying ? "Application…" : `Appliquer (${plural(selectedCount, "élément")})`}
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={requestClose} disabled={analyzing}>
              Annuler
            </Button>
            <Button
              variant="primary"
              onClick={() => void analyze()}
              disabled={analyzing || trimmed.length < MIN_LENGTH}
            >
              {analyzing ? "Analyse en cours…" : "Analyser"}
            </Button>
          </>
        )
      }
    >
      {confirmingClose ? (
        <p className="crm-extract__status">
          <Badge variant="warning">Abandonner</Badge>
          {extraction
            ? "L'analyse déjà effectuée et les cases cochées seront perdues."
            : "Le compte rendu collé sera perdu."}
        </p>
      ) : null}

      {reviewing && extraction ? (
        <div className="crm-extract">
          {extraction.summary ? <p className="crm-extract__summary">{extraction.summary}</p> : null}

          <ExtractGroup
            title="Informations du dossier"
            total={extraction.fields.length}
            selected={count(fieldSel)}
            onToggleAll={(next) => setAll(setFieldSel, next)}
          >
            {extraction.fields.map((field, i) => (
              <ExtractRow
                key={field.key}
                id={`${domId}-champ-${i}`}
                checked={fieldSel[i] ?? false}
                onToggle={() => toggleAt(setFieldSel, i)}
                label={field.label}
                value={field.display}
                conflict={field.conflict}
                current={field.currentDisplay}
                evidence={field.evidence}
                confidence={field.confidence}
              />
            ))}
          </ExtractGroup>

          <ExtractGroup
            title="Suivis à créer"
            total={extraction.tasks.length}
            selected={count(taskSel)}
            onToggleAll={(next) => setAll(setTaskSel, next)}
          >
            {extraction.tasks.map((task, i) => (
              <ExtractRow
                key={`${task.title}-${i}`}
                id={`${domId}-suivi-${i}`}
                checked={taskSel[i] ?? false}
                onToggle={() => toggleAt(setTaskSel, i)}
                kind="titre"
                label={task.title}
                value={`${CRM_TASK_TYPE_LABELS[task.type]} · ${
                  task.dueAt ? `échéance ${formatRelativeDay(task.dueAt)}` : "sans échéance"
                }`}
                valueTitle={task.dueAt ? formatDateTime(task.dueAt) : undefined}
                evidence={task.evidence}
                confidence={task.confidence}
              />
            ))}
          </ExtractGroup>

          <ExtractGroup
            title="Contacts"
            total={extraction.contacts.length}
            selected={count(contactSel)}
            onToggleAll={(next) => setAll(setContactSel, next)}
          >
            {extraction.contacts.map((contact, i) => (
              <ExtractRow
                key={`${contact.name}-${i}`}
                id={`${domId}-contact-${i}`}
                checked={contactSel[i] ?? false}
                onToggle={() => toggleAt(setContactSel, i)}
                kind="titre"
                label={contact.name}
                value={contactDetails(contact.role, contact.email, contact.phone)}
                conflict={contact.conflict}
                conflictBadge="doublon"
                evidence={contact.evidence}
              />
            ))}
          </ExtractGroup>

          {applying ? <p className="crm-extract__status">Application en cours…</p> : null}
          {failure ? (
            <p className="crm-extract__status">
              <Badge variant="danger">{failure.badge}</Badge> {failure.message}
            </p>
          ) : null}

          <p className="crm-extract__usage">
            Analyse — {plural(extraction.usage.inputTokens + extraction.usage.outputTokens, "jeton")}
          </p>
        </div>
      ) : (
        <div className="crm-extract">
          <p className="crm-extract__intro">
            Collez ici une transcription d'appel, un courriel ou vos notes de réunion. Le texte est
            analysé pour vous proposer ce qu'il établit sur le dossier : vous relisez chaque
            proposition et rien n'est enregistré sans votre validation.
          </p>
          <Textarea
            className="crm-extract__field"
            rows={15}
            autoFocus
            aria-label="Compte rendu à analyser"
            placeholder="Collez le compte rendu ici…"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          {analyzing ? (
            <p className="crm-extract__status">
              Analyse en cours… cela prend quelques secondes sur un long compte rendu.
            </p>
          ) : failure ? (
            <p className="crm-extract__status">
              <Badge variant="danger">{failure.badge}</Badge> {failure.message}
            </p>
          ) : trimmed.length > 0 && trimmed.length < MIN_LENGTH ? (
            <p className="crm-extract__status">
              Encore {plural(MIN_LENGTH - trimmed.length, "caractère")} avant de pouvoir analyser.
            </p>
          ) : null}
        </div>
      )}
    </Modal>
  );
}

interface ExtractGroupProps {
  title: string;
  total: number;
  selected: number;
  onToggleAll: (next: boolean) => void;
  children: ReactNode;
}

function ExtractGroup({ title, total, selected, onToggleAll, children }: ExtractGroupProps) {
  const all = total > 0 && selected === total;
  return (
    <section className="crm-extract__group">
      <div className="crm-extract__group-head">
        <h3 className="crm-extract__group-title">
          {title} <Badge variant="neutral">{total === 0 ? "0" : `${selected} / ${total}`}</Badge>
        </h3>
        {total > 0 ? (
          <label className="crm-extract__group-toggle">
            <input
              type="checkbox"
              checked={all}
              // Une sélection partielle ne doit pas se lire « rien de coché ».
              ref={(el) => {
                if (el) el.indeterminate = selected > 0 && !all;
              }}
              onChange={(e) => onToggleAll(e.target.checked)}
            />
            {all ? "Tout décocher" : "Tout cocher"}
          </label>
        ) : null}
      </div>
      {total === 0 ? (
        <p className="crm-extract__empty">Rien détecté dans ce compte rendu.</p>
      ) : (
        children
      )}
    </section>
  );
}

interface ExtractRowProps {
  id: string;
  checked: boolean;
  onToggle: () => void;
  /**
   * « champ » : le libellé est une étiquette de formulaire et la valeur porte
   * l'information. « titre » : c'est l'inverse — le nom du contact ou l'intitulé
   * du suivi est ce qu'on relit, le détail n'est qu'une précision.
   */
  kind?: "champ" | "titre";
  label: string;
  value: string;
  valueTitle?: string;
  conflict?: boolean;
  current?: string | null;
  conflictBadge?: string;
  evidence: string | null;
  confidence?: CrmExtractionConfidence;
}

function ExtractRow({
  id,
  checked,
  onToggle,
  kind = "champ",
  label,
  value,
  valueTitle,
  conflict = false,
  current,
  conflictBadge = "écrasement",
  evidence,
  confidence,
}: ExtractRowProps) {
  const classes = ["crm-extract__row", conflict && "crm-extract__row--conflict"]
    .filter(Boolean)
    .join(" ");
  // Les rôles typographiques s'échangent : l'étiquette reste l'étiquette de la
  // case à cocher, mais ce n'est plus elle qui doit se lire en petit et en gris.
  const labelClass = kind === "titre" ? "crm-extract__value" : "crm-extract__label";
  const valueClass = kind === "titre" ? "crm-extract__detail" : "crm-extract__value";

  return (
    <div className={classes}>
      <input
        id={id}
        className="crm-extract__check"
        type="checkbox"
        checked={checked}
        onChange={onToggle}
      />
      <div className="crm-extract__main">
        <label className={labelClass} htmlFor={id}>
          {label}
        </label>
        <span className={valueClass} title={valueTitle}>
          {value}
        </span>
        {conflict && current !== undefined ? (
          <span className="crm-extract__current">actuellement : {current ?? "—"}</span>
        ) : null}
        {evidence ? <p className="crm-extract__evidence">«&nbsp;{evidence}&nbsp;»</p> : null}
        {confidence || conflict ? (
          <span className="crm-extract__meta">
            {confidence ? (
              <Badge variant={CONFIDENCE_VARIANT[confidence]}>
                {CONFIDENCE_LABELS[confidence]}
              </Badge>
            ) : null}
            {conflict ? <Badge variant="warning">{conflictBadge}</Badge> : null}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function count(flags: boolean[]): number {
  return flags.filter(Boolean).length;
}

function toggleAt(set: Dispatch<SetStateAction<boolean[]>>, index: number): void {
  set((prev) => prev.map((v, i) => (i === index ? !v : v)));
}

function setAll(set: Dispatch<SetStateAction<boolean[]>>, next: boolean): void {
  set((prev) => prev.map(() => next));
}

/** Accord simple : tous les mots passés ici sont masculins. */
function plural(value: number, word: string): string {
  return `${value.toLocaleString("fr-CA")} ${word}${value > 1 ? "s" : ""}`;
}

function contactDetails(role: string | null, email: string | null, phone: string | null): string {
  const parts = [role, email, phone].filter((p): p is string => Boolean(p));
  return parts.length > 0 ? parts.join(" · ") : "Aucune coordonnée précisée";
}

function appliedSummary(applied: CrmExtractionApplied): string {
  const parts: string[] = [];
  if (applied.appliedFields > 0) parts.push(plural(applied.appliedFields, "champ"));
  if (applied.createdTasks > 0) parts.push(plural(applied.createdTasks, "suivi"));
  if (applied.createdContacts > 0) parts.push(plural(applied.createdContacts, "contact"));

  // Le reste a bien été écrit : l'utilisateur doit savoir ce qui manque à sa fiche.
  const rejected =
    applied.rejectedFields.length > 0
      ? ` Écarté${applied.rejectedFields.length > 1 ? "s" : ""} faute de valeur valide : ${applied.rejectedFields
          .map((f) => f.label)
          .join(", ")}.`
      : "";

  if (parts.length === 0) {
    return `Compte rendu archivé dans l'historique, sans modification.${rejected}`;
  }
  const total = applied.appliedFields + applied.createdTasks + applied.createdContacts;
  return `${parts.join(", ")} appliqué${total > 1 ? "s" : ""}.${rejected}`;
}

/** Clés que zod a refusées, telles que la route les remonte dans `details`. */
function rejectedKeys(err: unknown): string[] {
  if (!(err instanceof ApiError) || err.status !== 400) return [];
  const details = err.details;
  if (typeof details !== "object" || details === null) return [];
  const fieldErrors = (details as { fieldErrors?: unknown }).fieldErrors;
  if (typeof fieldErrors !== "object" || fieldErrors === null) return [];
  return Object.keys(fieldErrors);
}

function labelsFor(extraction: CrmExtraction, keys: string[]): string[] {
  return keys.map((key) => extraction.fields.find((f) => f.key === key)?.label ?? key);
}

/** Une panne inattendue mérite un toast ; un refus explicite du serveur se lit sur place. */
function isUnexpected(err: unknown): boolean {
  return !(err instanceof ApiError) || ![400, 404, 502, 503].includes(err.status);
}

function analyzeFailure(err: unknown): Failure {
  if (err instanceof ApiError) {
    if (err.status === 503) {
      return {
        badge: "Indisponible",
        message:
          "L'extraction automatique n'est pas configurée sur ce serveur (clé API absente). Vous pouvez saisir les informations à la main.",
      };
    }
    if (err.code === "extraction_too_long") {
      return {
        badge: "Trop long",
        message:
          "Ce compte rendu dépasse ce que l'analyse traite d'un seul bloc. Coupez-le en deux et relancez chaque moitié.",
      };
    }
    if (err.status === 502) {
      return {
        badge: "Échec",
        message: "L'analyse a échoué. Réessayez, ou saisissez les informations à la main.",
      };
    }
    if (err.status === 400) {
      return {
        badge: "Texte refusé",
        message: `Le compte rendu doit contenir entre ${MIN_LENGTH} et ${MAX_LENGTH.toLocaleString("fr-CA")} caractères.`,
      };
    }
    if (err.status === 404) {
      return { badge: "Introuvable", message: "Ce dossier n'existe plus. Rechargez la page." };
    }
  }
  return {
    badge: "Erreur",
    message: "L'analyse n'a pas pu aboutir. Réessayez dans un instant.",
  };
}

function applyFailure(err: unknown, rejected: string[]): Failure {
  if (err instanceof ApiError) {
    if (err.status === 400) {
      return {
        badge: "Refusé",
        message:
          rejected.length > 0
            ? `Le serveur a refusé ${rejected.join(", ")}. ${
                rejected.length > 1 ? "Ces lignes ont" : "Cette ligne a"
              } été décochée${rejected.length > 1 ? "s" : ""} : relancez l'application.`
            : "Le serveur a refusé une des valeurs proposées. Décochez la ligne en cause, puis réessayez.",
      };
    }
    if (err.status === 404) {
      return { badge: "Introuvable", message: "Ce dossier n'existe plus. Rechargez la page." };
    }
  }
  return {
    badge: "Erreur",
    message: "L'import n'a pas pu aboutir. Rien n'a été enregistré, réessayez dans un instant.",
  };
}
